import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BasicLayout, scrollUp, useStepper } from '@monorepo/common';

import { Step1 } from './steps/Steps1';
import { Step2 } from './steps/Steps2';
import { Step3 } from './steps/Steps3';

import { MetaDescription } from '../../components/MetaDescription';
import PackagesHeaderBand from './components/PackagesHeaderBand';
import CrossBrandBanner from '../../components/CrossBrandBanner';
import { IStepsOneInterfaces } from './stepsOneInterfaces';
import { useAppSelector } from '../../store/hooks';
import FinalBlock from './components/FinalBlock';
import { useCreateJobPackageMutation, useGetJobPackagesQuery, useGetPaymentIntentPrepaidPurchaseMutation } from '../../services/servicePrepaidPurchase';
import { buildPrepaidIntentBody, interpretIntentResponse, GENERIC_INTENT_ERROR } from '../../components/JobPayment';
import { IStep1, IStep2, IStep3AllDataProps } from './interfaceSteps';
import { showNotification } from '../../features/sliceNotification';
import { emptyObj, staticValuesForStep1, staticValuesForStep2, staticValuesForStep3 } from './staticValues';
import { setPageTitle } from '../../utils/setPageTitle';
import { readUtmCookie } from '../../utils/utm';
import { Hosts, getListingsPerPack } from '../../constants/hostName';
import { TOKENS } from '../../styles/tokens';
import { gtmPush } from '../../utils/gtm';
import { hsIdentify, hsPush } from '../../utils/hubspot';
import { useGetUserInfoQuery } from '../../services/serviceUserInfo';

export const PrepaidPurchase = () => {
  const dispatch = useDispatch();
  const { data: userInfo } = useGetUserInfoQuery();
  const [titleBrowserPage, setTitleBrowserPage] = useState<string>('Jobs');

  const { hostNumber, isHost, siteParams } = useAppSelector(({ pathnameSettings }) => ({
    hostNumber: pathnameSettings.siteNumber.toString(),
    siteParams: pathnameSettings,
    isHost: pathnameSettings.host
  }));

  // Seed the applied-coupon state from the URL `?code=` param (read once).
  const urlCode = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code') || undefined;
  }, []);

  // Editable applied coupon: seeded from the URL, then updated when the user
  // Applies or clears the Code field. Drives the pricing query + step1.couponCode.
  const [appliedCoupon, setAppliedCoupon] = useState<string | undefined>(urlCode);

  const { data: jobPackages, isLoading, isFetching } = useGetJobPackagesQuery({ id: hostNumber, code: appliedCoupon }, {
    skip: !isHost
  });

  // for steps
  const [dataForStep1, setDataForStep1] = useState<IStepsOneInterfaces[]>([]);
  const [step1, setStep1] = useState<IStep1>(staticValuesForStep1);
  const [step2, setStep2] = useState<IStep2>(staticValuesForStep2);
  const [step3] = useState<IStep3AllDataProps>(staticValuesForStep3);

  const [createJobPackage, { data, isLoading: isPaymentLoading, error: finalResponseError, isSuccess: finalResponseSuccess }] = useCreateJobPackageMutation();
  const { putSteps, onNext, onBack, onComplete, renderStepper, StepsButtons, setIconColor } = useStepper({
    orientation: 'vertical',
    // The old left panel is gone (PackagesHeaderBand renders above the stepper, like
    // PostJobListing). leftInfoPanel is a REQUIRED field on IUseStepperProps (common is
    // off-limits — Magazine consumes the hook), so the key must be present; pass it
    // undefined — ReactNode includes undefined and InfoPanelContainer returns null for
    // falsy children, so the built-in panel collapses.
    leftInfoPanel: undefined,
    finalComponent: <FinalBlock data={data} />,
    // Same centered ~900px stepper column as PostJobListing (inline style beats the
    // 50rem wrapper class).
    styleProps: { widthAllStepper: 56.25 },
  });

  const finalPaymentRequest = (result: IStep3AllDataProps) => {
    return createJobPackage({
      id: hostNumber,
      validateOnly: false,
      body: {
        step1,
        step2,
        step3: result,
        // Last-touch UTM attribution: read the shared `cpg_utm` cookie at conversion
        // and send it as a top-level sibling of the steps to POST /Jobs/PrepaidPurchase.
        // undefined (no UTM captured) is dropped by JSON serialization → backend null.
        utmParams: readUtmCookie(),
      }
    });
  };

  // PaymentIntent ownership (see §Timing). siteId = hostNumber (prepaid has no
  // isUrlApproach split). Recreated when package quantities / coupon change.
  const [reachedPayment, setReachedPayment] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [getPaymentIntent] = useGetPaymentIntentPrepaidPurchaseMutation();

  const createIntent = useCallback(async () => {
    setIntentError(null);
    const result = await getPaymentIntent(buildPrepaidIntentBody({
      standardPackagesQuantity: step1.standardPackagesQuantity,
      featuredPackagesQuantity: step1.featuredPackagesQuantity,
      siteId: hostNumber,
      couponCode: step1.couponCode
    }));
    if ('data' in result) {
      const outcome = interpretIntentResponse(result.data);
      setClientSecret(outcome.clientSecret);
      setIntentError(outcome.error);
    } else {
      const err = (result.error as { data?: { error?: string } })?.data?.error;
      setClientSecret(null);
      setIntentError(err || GENERIC_INTENT_ERROR);
    }
  }, [getPaymentIntent, step1.standardPackagesQuantity, step1.featuredPackagesQuantity, step1.couponCode, hostNumber]);

  useEffect(() => {
    if (reachedPayment) {
      createIntent();
    }
  }, [reachedPayment, createIntent]);

  const steps = useMemo(() => [
    {
      label: 'Select Package',
      content: (
        <Step1
          setPackages={setDataForStep1}
          list={dataForStep1}
          onNext={onNext}
          Nav={StepsButtons}
          jobPackages={jobPackages}
          couponCode={appliedCoupon}
          isCouponLoading={isLoading}
          isCouponFetching={isFetching}
          onApplyCoupon={setAppliedCoupon}
        />
      )
    },
    {
      label: 'Contact Information',
      content: (
        <Step2
          step2={step2 || null}
          setStep2={setStep2}
          onBack={onBack}
          onNext={() => {
            setReachedPayment(true);
            onNext();
          }}
          Nav={StepsButtons}
        />
      )
    },
    {
      label: 'Payment Information',
      content: (
        <Step3
          step1={step1}
          step2={step2}
          step3={step3}
          onBack={onBack}
          Nav={StepsButtons}
          onNext={onComplete}
          dataForStep1={dataForStep1}
          finalPaymentRequest={finalPaymentRequest}
          clientSecret={clientSecret}
          intentError={intentError}
          retryIntent={createIntent}
        />
      )
    }
  ], [StepsButtons, step1, step2, step3, dataForStep1, jobPackages, clientSecret, intentError, createIntent, appliedCoupon, isLoading, isFetching]);

  useEffect(() => {
    // Re-publish steps when intent state changes too — useStepper renders a
    // snapshot from putSteps, not live children, so Steps3 needs the fresh secret.
    putSteps(steps);
  }, [step1, step2, step3, dataForStep1, clientSecret, intentError, createIntent, appliedCoupon, isLoading, isFetching]);

  useEffect(() => {
    if (jobPackages && jobPackages.prepaidPackages) {
      // Applying/removing a coupon re-fetches PrepaidPackages, returning fresh
      // package objects (updated prices) that carry NO user quantity. Rebuilding
      // blindly reset every `number` to 0 (emptyObj default), collapsing the row
      // LISTINGS/PRICE and the order TOTAL to 0 — while the qty <input> (uncontrolled
      // `defaultValue`) kept showing the old value — which disabled "Next step" and
      // blocked checkout (D-24094). Preserve the quantity the user already picked,
      // matched by the stable `description`, so the TOTAL reflects the discounted
      // per-pack price. On the initial load `prev` is empty → identical to before.
      setDataForStep1((prev) => jobPackages.prepaidPackages.map((item) => {
        const existing = prev.find((p) => p.description === item.description);
        return {
          ...emptyObj,
          ...item,
          // Per-pack listings depend on the site: CPG Media grants 5, others 6.
          // The API item carries only price, so set it here (overrides emptyObj default).
          listingsNumber: getListingsPerPack(siteParams.siteNumber),
          ...(existing ? { number: existing.number } : {})
        };
      }));
    }
  }, [jobPackages, siteParams.siteNumber]);

  useEffect(() => {
    setPageTitle({
      firstTitle: 'Job Listing Packages',
      siteNumber: siteParams.siteNumber || Hosts.BEVNET,
      setTitle: setTitleBrowserPage
    });
  }, [siteParams]);

  useEffect(() => {
    const localFormattedData: IStep1 = {
      ...staticValuesForStep1,
      couponCode: appliedCoupon
    };
    if (dataForStep1.length) {
      dataForStep1.forEach((item:IStepsOneInterfaces) => {
        // `?? 0` so an untouched pack (item.number undefined) does not override the
        // staticValues default of 0 with undefined — that would drop the quantity
        // from both the PI body AND the finalize body (D-34). 0 = "none of this pack".
        if (item.description.search(/standard/i) >= 0) {
          return localFormattedData.standardPackagesQuantity = item.number ?? 0;
        }
        if (item.description.search(/featured/i) >= 0) {
          return localFormattedData.featuredPackagesQuantity = item.number ?? 0;
        }
        return localFormattedData;
      });
      setStep1(localFormattedData);
    }
  }, [dataForStep1, appliedCoupon]);

  useEffect(() => {
    if (finalResponseError && 'data' in finalResponseError) {
      const resp = finalResponseError as { data: { error: string } };
      scrollUp();
      dispatch(showNotification({
        show: true,
        type: 'error',
        text: resp.data.error
      }));
    }
    if (finalResponseSuccess) {
      scrollUp();
      // GA4 conversion event. The create-package response carries no amount, so
      // this is a custom event with the transaction id only — promote it to a
      // full ecommerce purchase if the API ever returns the order total.
      gtmPush({ event: 'prepaid_purchase_complete', transaction_id: String(data?.orderId ?? '') });
      // Tie the purchaser to their HubSpot contact and flush — identify only
      // sends with the next tracked event.
      if (userInfo?.email) {
        hsIdentify(userInfo.email, {
          firstname: userInfo.firstName || undefined,
          lastname: userInfo.lastName || undefined,
        });
        hsPush('trackPageView');
      }
      onComplete();
    }
  }, [finalResponseError, finalResponseSuccess]);

  useEffect(() => {
    setIconColor(TOKENS.accent);
  }, [siteParams]);

  return (
    <BasicLayout
      dataCy="PrepaidPurchase"
      isLoading={isPaymentLoading}
      helmet={MetaDescription({ title: titleBrowserPage })}
    >
      {/* Only reassure visitors arriving from a named brand board (/nosh,
          /brewbound, /bevnet). Bare /PrepaidPurchase has no source brand, so
          the cross-brand strip is suppressed (pathname is null there). */}
      {Boolean(siteParams.pathname) && <CrossBrandBanner />}
      <PackagesHeaderBand />
      {renderStepper}
    </BasicLayout>
  );
};
